import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MobileStickyBar from '@/components/feature/MobileStickyBar';
import useSEO from '@/hooks/useSEO';

const FreeQuotePage = () => {
  useSEO({
    title: 'Free Glass Estimate Las Vegas | Quinolux Glass & Mirrors',
    description: 'Get a free, no-obligation glass estimate in Las Vegas. Frameless shower doors, glass railings, custom mirrors & more. Fast response. Call (702) 604-2093 or fill out our form.',
    keywords: 'free glass estimate Las Vegas, free shower door quote Las Vegas, glass installation quote Las Vegas',
    canonical: 'https://quinoluxglass.com/free-quote',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: 'Free Glass Estimate Las Vegas | Quinolux Glass & Mirrors',
        description: 'Get a free, no-obligation glass estimate in Las Vegas. Frameless shower doors, glass railings, custom mirrors & more.',
        url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/free-quote`,
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/` },
            { '@type': 'ListItem', position: 2, name: 'Free Quote', item: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/free-quote` },
          ],
        },
        potentialAction: {
          '@type': 'RequestQuoteAction',
          target: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}/free-quote`,
          name: 'Request a Free Glass Estimate',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: 'Free Glass Installation Estimate — Las Vegas',
        description: 'Free, no-obligation glass installation estimates for frameless shower doors, glass railings, custom mirrors, tub enclosures, and more across Las Vegas Valley.',
        provider: {
          '@type': 'LocalBusiness',
          name: 'Quinolux Glass & Mirrors',
          telephone: '+17026042093',
          url: `${import.meta.env.VITE_SITE_URL ?? 'https://quinoluxglass.com'}`,
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Las Vegas',
            addressRegion: 'NV',
            addressCountry: 'US',
          },
        },
        areaServed: [
          { '@type': 'City', name: 'Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Henderson', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Summerlin', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'North Las Vegas', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Spring Valley', containedInPlace: { '@type': 'State', name: 'Nevada' } },
          { '@type': 'City', name: 'Paradise', containedInPlace: { '@type': 'State', name: 'Nevada' } },
        ],
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free, no-obligation estimate for glass installation services.',
          availability: 'https://schema.org/InStock',
        },
        serviceType: 'Glass Installation Estimate',
        hasOfferCatalog: {
          '@type': 'OfferCatalog',
          name: 'Glass Services',
          itemListElement: [
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Frameless Shower Doors' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Sliding Glass Doors' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Tub Enclosures' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Glass Railings' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Custom Mirrors' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Glass Partitions' } },
            { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Window Glass Replacement' } },
          ],
        },
      },
    ],
  });

  const [formData, setFormData] = useState({ name: '', phone: '', email: '', service: '', city: '', budget: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      await fetch('https://readdy.ai/api/form/d7eoj25fi84lst2k0890', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch { setSubmitted(true); }
    finally { setSubmitting(false); }
  };

  const services = ['Frameless Shower Doors', 'Sliding Glass Doors', 'Tub Enclosures', 'Glass Railings', 'Stair Glass', 'Custom Mirrors', 'Glass Partitions', 'Window Glass Replacement', 'Handyman Services', 'Other'];
  const cities = ['Las Vegas', 'Henderson', 'Summerlin', 'North Las Vegas', 'Spring Valley', 'Paradise', 'Boulder City', 'Other'];
  const budgets = ['Under $500', '$500 – $1,000', '$1,000 – $2,500', '$2,500 – $5,000', '$5,000+', 'Not sure yet'];

  return (
    <main>
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://public.readdy.ai/ai/img_res/2f41979d43fbffaefc04d967eb7ba245.jpg" alt="" className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-gray-300 mb-4 block">No Obligation</span>
          <h1 className="text-5xl md:text-6xl font-light text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Get Your Free Estimate
          </h1>
          <p className="text-gray-300 text-base max-w-xl mx-auto">
            Fill out the form below and we'll contact you within 24 hours with a detailed, no-obligation estimate for your glass project.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            {[{ icon: 'ri-time-line', text: 'Response within 24 hours' }, { icon: 'ri-shield-check-line', text: 'No obligation, ever' }, { icon: 'ri-map-pin-line', text: 'Free in-home measurement' }].map(b => (
              <div key={b.text} className="flex items-center gap-2 text-white/80 text-sm">
                <i className={`${b.icon} text-white`}></i>
                {b.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="py-16 bg-[#F8F8F6]">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-3 mb-10">
            {[1, 2, 3].map(s => (
              <div key={s} className="flex items-center gap-3">
                <div className={`w-8 h-8 flex items-center justify-center rounded-full text-xs font-bold transition-all duration-300 ${step >= s ? 'bg-black text-white' : 'bg-gray-200 text-gray-500'}`}>{s}</div>
                {s < 3 && <div className={`w-12 h-0.5 transition-all duration-300 ${step > s ? 'bg-black' : 'bg-gray-200'}`} />}
              </div>
            ))}
          </div>

          {submitted ? (
            <div className="bg-white rounded-3xl p-12 text-center">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-black text-white mx-auto mb-6">
                <i className="ri-check-line text-3xl"></i>
              </div>
              <h2 className="text-3xl font-light text-black mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Request Received!</h2>
              <p className="text-gray-500 text-sm mb-8">We'll contact you within 24 hours with your free estimate. In the meantime, feel free to call us directly.</p>
              <a href="tel:+17026042093" className="inline-flex items-center gap-2 bg-black text-white text-sm font-semibold px-8 py-4 rounded-full hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                <i className="ri-phone-fill"></i> Call (702) 604-2093
              </a>
            </div>
          ) : (
            <form data-readdy-form id="free-quote-form" onSubmit={handleSubmit} className="bg-white rounded-3xl p-8 md:p-10">
              {step === 1 && (
                <div className="flex flex-col gap-5">
                  <div>
                    <h2 className="text-2xl font-semibold text-black mb-1">Your Contact Info</h2>
                    <p className="text-gray-400 text-sm mb-6">We'll use this to send you your estimate.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">Full Name *</label>
                      <input type="text" name="name" required value={formData.name} onChange={handleChange} placeholder="John Smith" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-gray-600 mb-1.5 block">Phone *</label>
                      <input type="tel" name="phone" required value={formData.phone} onChange={handleChange} placeholder="(702) 000-0000" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Email *</label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder="john@example.com" className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors" />
                  </div>
                  <button type="button" onClick={() => { if (formData.name && formData.phone && formData.email) setStep(2); }} className="w-full bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer mt-2">
                    Next Step <i className="ri-arrow-right-line ml-1"></i>
                  </button>
                </div>
              )}

              {step === 2 && (
                <div className="flex flex-col gap-5">
                  <div>
                    <h2 className="text-2xl font-semibold text-black mb-1">Project Details</h2>
                    <p className="text-gray-400 text-sm mb-6">Tell us about your glass project.</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Service Needed *</label>
                    <select name="service" required value={formData.service} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer">
                      <option value="">Select a service...</option>
                      {services.map(s => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Your City *</label>
                    <select name="city" required value={formData.city} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer">
                      <option value="">Select your city...</option>
                      {cities.map(c => <option key={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Approximate Budget</label>
                    <select name="budget" value={formData.budget} onChange={handleChange} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors cursor-pointer">
                      <option value="">Select budget range...</option>
                      {budgets.map(b => <option key={b}>{b}</option>)}
                    </select>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <button type="button" onClick={() => setStep(1)} className="flex-1 border border-gray-200 text-black text-sm font-medium py-4 rounded-xl hover:border-black transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-left-line mr-1"></i> Back
                    </button>
                    <button type="button" onClick={() => { if (formData.service && formData.city) setStep(3); }} className="flex-1 bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
                      Next Step <i className="ri-arrow-right-line ml-1"></i>
                    </button>
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="flex flex-col gap-5">
                  <div>
                    <h2 className="text-2xl font-semibold text-black mb-1">Additional Details</h2>
                    <p className="text-gray-400 text-sm mb-6">Any extra info helps us give you a more accurate estimate.</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">Project Description</label>
                    <textarea name="message" value={formData.message} onChange={handleChange} placeholder="Describe your project — dimensions, style preferences, timeline, etc." rows={5} maxLength={500} className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-black transition-colors resize-none" />
                    <p className="text-gray-400 text-xs mt-1">{formData.message.length}/500 characters</p>
                  </div>
                  {/* Summary */}
                  <div className="bg-[#F8F8F6] rounded-xl p-4 text-sm">
                    <p className="font-semibold text-black mb-2">Your Request Summary:</p>
                    <p className="text-gray-600"><span className="text-gray-400">Name:</span> {formData.name}</p>
                    <p className="text-gray-600"><span className="text-gray-400">Service:</span> {formData.service}</p>
                    <p className="text-gray-600"><span className="text-gray-400">City:</span> {formData.city}</p>
                  </div>
                  <div className="flex gap-3 mt-2">
                    <button type="button" onClick={() => setStep(2)} className="flex-1 border border-gray-200 text-black text-sm font-medium py-4 rounded-xl hover:border-black transition-colors whitespace-nowrap cursor-pointer">
                      <i className="ri-arrow-left-line mr-1"></i> Back
                    </button>
                    <button type="submit" disabled={submitting} className="flex-1 bg-black text-white text-sm font-semibold py-4 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60">
                      {submitting ? 'Sending...' : 'Submit Request'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}

          {/* Trust signals */}
          <div className="flex flex-wrap items-center justify-center gap-6 mt-8">
            {[
              { icon: 'ri-star-fill', text: '500+ 5-Star Reviews' },
              { icon: 'ri-time-line', text: '15+ Years Experience' },
              { icon: 'ri-shield-check-line', text: 'Insured & Bonded' },
              { icon: 'ri-tools-line', text: 'Handyman Services' },
            ].map(t => (
              <div key={t.text} className="flex items-center gap-2 text-gray-500 text-xs">
                <i className={`${t.icon} text-black`}></i>
                {t.text}
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <MobileStickyBar />
    </main>
  );
};

export default FreeQuotePage;
